import React, { PureComponent } from 'react';
import { SidePanelContext } from '../SidePanelAPI';
import PropTypes from 'prop-types';
import { st, classes } from './Header.st.css';
import { dataHooks } from '../constants';
import Heading from '../../Heading';
import Divider from '../../Divider';
import CloseButton from '../../CloseButton';
import Box from '../../Box';
import InfoIcon from '../../InfoIcon';
import Text from '../../Text';
import { TooltipCommonProps } from '../../common/PropTypes/TooltipCommon';
import { WixStyleReactContext } from '../../WixStyleReactProvider/context';
import { HeaderProps } from './Header.types';
import { SidePanelProps } from '../SidePanel.types';
import { Help24 as Help } from '@wix/wix-ui-icons-common/system';

class Header extends PureComponent<HeaderProps> {
  static displayName = 'SidePanel.Header';

  static propTypes = {
    className: PropTypes.string,
    title: PropTypes.node,
    subtitle: PropTypes.node,
    infoTooltipContent: PropTypes.node,
    infoTooltipProps: PropTypes.shape(TooltipCommonProps),
    showDivider: PropTypes.bool,
    children: PropTypes.node,
    maxLines: PropTypes.number,
    ellipsis: PropTypes.bool,
  };

  static defaultProps = {
    showDivider: true,
  };

  renderStringTitle() {
    const { title, infoTooltipContent, infoTooltipProps, maxLines, ellipsis } =
      this.props;

    return (
      <Box>
        <Heading
          placement="auto"
          ellipsis={ellipsis}
          maxLines={maxLines}
          size="medium"
          as="h2"
          dataHook={dataHooks.sidePanelHeaderTitle}
        >
          {title}
        </Heading>
        {infoTooltipContent && (
          <Box marginLeft="tiny">
            <InfoIcon
              dataHook={dataHooks.sidePanelHeaderTitleInfoIcon}
              content={infoTooltipContent}
              tooltipProps={infoTooltipProps}
            />
          </Box>
        )}
      </Box>
    );
  }

  renderSubtitle() {
    const { subtitle } = this.props;
    if (!subtitle) return null;

    const isStringSubtitle = typeof subtitle === 'string';

    if (!isStringSubtitle) {
      return subtitle;
    }

    return (
      <Text
        skin="standard"
        secondary
        dataHook={dataHooks.sidePanelHeaderSubtitle}
      >
        {subtitle}
      </Text>
    );
  }

  renderTitle(
    onCloseButtonClick?: SidePanelProps['onCloseButtonClick'],
    onHelpButtonClick?: SidePanelProps['onHelpButtonClick'],
  ) {
    const { title } = this.props;
    const isStringTitle = typeof title === 'string';

    return (
      <div
        className={st(classes.titleContainer, {
          noCloseButton: !onCloseButtonClick,
        })}
      >
        <div className={classes.title}>
          {isStringTitle ? this.renderStringTitle() : title}
          {this.renderSubtitle()}
        </div>

        {this.renderButtons(onCloseButtonClick, onHelpButtonClick)}
      </div>
    );
  }

  renderButtons(
    onCloseButtonClick?: SidePanelProps['onCloseButtonClick'],
    onHelpButtonClick?: SidePanelProps['onHelpButtonClick'],
  ) {
    if (!onCloseButtonClick && !onHelpButtonClick) {
      return <></>;
    }

    return (
      <div className={classes.buttonBox}>
        {onHelpButtonClick && (
          // @ts-ignore
          <CloseButton
            dataHook={dataHooks.sidePanelHeaderHelpButton}
            onClick={onHelpButtonClick}
            size="large"
            skin="dark"
            children={<Help className={classes.helpIcon} />}
          />
        )}
        {onCloseButtonClick && (
          // @ts-ignore
          <CloseButton
            dataHook={dataHooks.sidePanelHeaderCloseButton}
            className={classes.closeButton}
            onClick={onCloseButtonClick}
            size="large"
            skin="dark"
          />
        )}
      </div>
    );
  }

  render() {
    const { children, showDivider, className } = this.props;
    return (
      <SidePanelContext.Consumer>
        {({ skin, isDraggable, onCloseButtonClick, onHelpButtonClick }) => (
          <WixStyleReactContext.Consumer>
            {({ newColorsBranding }) => (
              <div
                className={st(
                  classes.root,
                  { newColorsBranding, skin, isDraggable },
                  className,
                )}
                data-hook={dataHooks.sidePanelHeader}
              >
                {this.renderTitle(onCloseButtonClick, onHelpButtonClick)}
                {children}
                {showDivider && (
                  // @ts-ignore
                  <Divider dataHook={dataHooks.sidePanelHeaderDivider} />
                )}
              </div>
            )}
          </WixStyleReactContext.Consumer>
        )}
      </SidePanelContext.Consumer>
    );
  }
}

export default Header;
